"use strict";

import SessionManager from "session-mgmt";
import cookieToken from "./cookie-token";
import jwt_decode from "jwt-decode";

function fromCallback() {
  return window.location.href.includes("oauth/callback");
}

const LocalStoragePort = require("./local-storage.js");
const returnUrlKey = "hint.redirect-after-login";
let clockOffsetMs = 0;

function persistRedirect(forcedUrl) {
  const defaultUrl = window.location.href;
  if (
    forcedUrl !== undefined ||
    (!fromCallback() &&
      !["login", "logout", "callback", "join"].some((s) =>
        defaultUrl.includes(s),
      ))
  ) {
    let url = forcedUrl || defaultUrl;
    LocalStoragePort.setItem(returnUrlKey, url);
  }
}

const sessionSDK = new SessionManager({
  postLogoutRedirectURI: "/login",
  loginUrl: CONFIGURATION.authUrl,
  isDevelopment: CONFIGURATION.debugOrTest,
  logger: true,
  onLogout: function () {
    cookieToken.remove();
    window.dispatchEvent(new Event("session:loggedout"));
    persistRedirect();
  },
});

const FronteggPort = {
  add: function (app, clientId, authUrl) {
    const fronteggApp = Frontegg.initialize({
      contextOptions: {
        baseUrl: authUrl,
        clientId: clientId,
        tenantResolver: () => {
          const urlQueryParams = new URLSearchParams(window.location.search);
          let organization = urlQueryParams.get("organization");
          if (organization) {
            LocalStoragePort.setItem("hint.custom-organization", organization);
          } else {
            organization = LocalStoragePort.getItem("hint.custom-organization");
          }

          return {
            tenant: organization,
          };
        },
      },
      authOptions: {
        keepSessionAlive: true,
      },
      hostedLoginBox: true,
    });

    app.ports.fronteggAuthorizePort.subscribe(function () {
      subscribeToAuth();
      loginWithRedirect();
    });

    app.ports.fronteggLoginPort.subscribe(function () {
      subscribeToAuth();
      if (!fromCallback()) {
        loginWithRedirect();
      }
    });

    if (app.ports.fronteggRefreshSessionPort) {
      app.ports.fronteggRefreshSessionPort.subscribe(function () {
        loginWithRedirect();
      });
    }

    app.ports.fronteggPersistRedirectPort.subscribe(function (url) {
      persistRedirect(url);
    });

    function loginWithRedirect() {
      if (!CONFIGURATION.skipSSOInit) {
        fronteggApp.ready(() => {
          fronteggApp.loginWithRedirect();
        });
      } else {
        if (cookieToken.load()) {
          window.dispatchEvent(new Event("session:updated"));
        }
      }
    }

    window.addEventListener("fronteggRefreshSession", function () {
      loginWithRedirect();
    });

    function retrieveReturnUrl() {
      return LocalStoragePort.getItem(returnUrlKey);
    }

    function onAccessTokenChange(accessToken) {
      const isNewToken = !cookieToken.load();
      clockOffsetMs = jwt_decode(accessToken).iat * 1000 - Date.now();
      cookieToken.set(accessToken);

      if (isNewToken || fromCallback()) {
        LocalStoragePort.removeItem("hint.custom-organization");
        const eventData = {
          detail: {
            accessToken: accessToken,
            redirectToURI: retrieveReturnUrl(),
          },
        };
        window.dispatchEvent(
          new CustomEvent("session-token:created", eventData),
        );
        LocalStoragePort.removeItem(returnUrlKey);
      } else {
        window.dispatchEvent(new Event("session:updated"));
      }

      app.ports.fronteggPortOnAccessTokenChanged.send(null);
    }

    function onUnAuthenticated() {
      persistRedirect();
      if (!fromCallback()) {
        loginWithRedirect();
      }
    }

    function subscribeToAuth() {
      sessionSDK.run({
        app: fronteggApp,
        onExpirationWarning: function () {
          window.dispatchEvent(new Event("session:warning"));
        },
        onUnAuthenticated: onUnAuthenticated,
        onAccessTokenChange: onAccessTokenChange,
      });
    }

    function isTokenExpired(accessToken) {
      let decoded = jwt_decode(accessToken);
      const refreshInAdvanceMs = 5000;
      if (
        Date.now() + clockOffsetMs + refreshInAdvanceMs >=
        decoded.exp * 1000
      ) {
        return true;
      }
      return false;
    }

    function throttle(func, timeFrame) {
      var lastTime = 0;
      return function () {
        var now = Date.now();
        if (now - lastTime >= timeFrame) {
          func();
          lastTime = now;
        }
      };
    }

    document.addEventListener(
      "mousemove",
      throttle(function () {
        if (cookieToken.load() && isTokenExpired(cookieToken.load())) {
          console.log("The session is expired, refreshing manually");
          loginWithRedirect();
        }
      }, 2000),
    );
  },

  logout: function () {
    sessionSDK.logout();
  },
};

module.exports = FronteggPort;
